import type { Editor } from '@tiptap/react';
import { EditorContent as TiptapEditorContent } from '@tiptap/react';
import React, { useEffect } from 'react';

import { handleBlockquoteBackspace } from '../handlers/blockquoteHandlers';
import { handleListBackspace, handleListTabIndentation } from '../handlers/listHandlers';

export interface EditorContentProps {
  editor: Editor | null;
  height: string;
  keyboardHandlers: {
    handleDeleteKeyAfterMention: (event: KeyboardEvent) => boolean;
    handleSendShortcut?: (event: KeyboardEvent) => boolean;
  };
  className?: string;
}

/**
 * EditorContent
 * Wrapper around Tiptap's EditorContent with custom styling and event handling
 */
export const EditorContent: React.FC<EditorContentProps> = ({
  editor,
  height,
  keyboardHandlers,
  className = '',
}) => {
  useEffect(() => {
    if (!editor) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const listTabHandled = handleListTabIndentation(editor, event);
      if (listTabHandled) {
        return;
      }

      if (keyboardHandlers.handleSendShortcut) {
        const sendHandled = keyboardHandlers.handleSendShortcut(event);
        if (sendHandled) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }
      }

      if (event.key === 'Backspace') {
        const listHandled = handleListBackspace(editor, event);
        if (listHandled) {
          return;
        }

        const blockquoteHandled = handleBlockquoteBackspace(editor, event);
        if (blockquoteHandled) {
          return;
        }

        const mentionHandled = keyboardHandlers.handleDeleteKeyAfterMention(event);
        if (mentionHandled) {
          event.preventDefault();
          return;
        }
      }

      if (event.key === 'Delete') {
        const handled = keyboardHandlers.handleDeleteKeyAfterMention(event);
        if (handled) {
          event.preventDefault();
          return;
        }
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('keydown', handleKeyDown, true);

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [editor, keyboardHandlers]);

  return (
    <div
      className='relative flex-1 overflow-hidden'
      style={{ height }}
    >
      <TiptapEditorContent
        editor={editor}
        className={`h-full overflow-y-auto focus:outline-none ${className}`}
      />
    </div>
  );
};
